import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatTimeRange, formatFullDateTime12h, formatDateMMDDYYYY } from '../../utils/timeFormat';
import {
  ArrowLeft, FileText, Camera, GraduationCap, BarChart3, Calendar,
  MapPin, Users, CheckCircle2, Clock, Eye, Download, Star, X,
  ChevronRight, Image, AlertTriangle, Search
} from 'lucide-react';

const ClientReports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [eventMedia, setEventMedia] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaDetail, setMediaDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [shiftsRes, mediaRes, quizRes] = await Promise.all([
        api.get('/client/shifts'),
        api.get('/events/analytics').catch(() => ({ data: { events: [] } })),
        api.get('/quizzes/results/all').catch(() => ({ data: { results: [] } }))
      ]);
      setShifts(shiftsRes.data || []);
      setEventMedia(mediaRes.data?.events || []);
      setQuizResults(quizRes.data?.results || quizRes.data || []);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchMediaDetail = async (gigId) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/events/analytics/${gigId}`);
      setMediaDetail(res.data);
    } catch (error) {
      toast.error('Failed to load event photos');
    } finally {
      setLoadingDetail(false);
    }
  };

  const filteredShifts = shifts.filter(s =>
    !searchQuery || s.venue_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.role_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const completedShifts = shifts.filter(s => s.status === 'completed' || s.shift_date < new Date().toISOString().split('T')[0]);
  const totalMedia = eventMedia.reduce((sum, e) => sum + (e.media_count || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-reports-page">
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/client/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Event Reports</h1>
                <p className="text-sm text-slate-500">Photos, recaps, and training results</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-5 h-5 text-cyan-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-slate-900" data-testid="total-events">{shifts.length}</p>
              <p className="text-xs text-slate-500">Total Events</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-slate-900" data-testid="completed-events">{completedShifts.length}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <Camera className="w-5 h-5 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-slate-900" data-testid="total-photos">{totalMedia}</p>
              <p className="text-xs text-slate-500">Event Photos</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <GraduationCap className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-slate-900" data-testid="total-quiz-results">{Array.isArray(quizResults) ? quizResults.length : 0}</p>
              <p className="text-xs text-slate-500">Training Results</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Event Overview</TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos">Event Photos</TabsTrigger>
            <TabsTrigger value="training" data-testid="tab-training">Training Results</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search events by name or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-events"
                />
              </div>
            </div>
            <div className="space-y-3">
              {filteredShifts.length === 0 ? (
                <Card className="bg-white">
                  <CardContent className="py-12 text-center text-slate-400">
                    <FileText className="w-10 h-10 mx-auto mb-3" />
                    <p>No events found</p>
                  </CardContent>
                </Card>
              ) : (
                filteredShifts.map((shift) => {
                  const hasMedia = eventMedia.some(e => e.gig_id === shift.id && e.media_count > 0);
                  const isPast = shift.shift_date < new Date().toISOString().split('T')[0];
                  return (
                    <Card key={shift.id} className="bg-white hover:shadow-md transition-shadow" data-testid="event-card">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{shift.venue_name}</h3>
                              {isPast && (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Completed</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {formatDateMMDDYYYY(shift.shift_date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {formatTimeRange(shift.start_time, shift.end_time)}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {shift.city}, {shift.state}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {shift.workers_needed || shift.booked_count || 0} staff
                              </span>
                            </div>
                            {/* Quick action badges */}
                            <div className="flex gap-2 mt-2">
                              {hasMedia && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                  <Camera className="w-3 h-3 mr-1" />Photos
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 ml-3">
                            {isPast && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/client/recap/${shift.id}`)}
                                  data-testid="view-recap-btn"
                                >
                                  <FileText className="w-3.5 h-3.5 mr-1" />
                                  Recap
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/client/job-overview/${shift.id}`)}
                                  data-testid="view-overview-btn"
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1" />
                                  Details
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos">
            {eventMedia.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center text-slate-400">
                  <Image className="w-10 h-10 mx-auto mb-3" />
                  <p className="font-medium">No event photos yet</p>
                  <p className="text-sm mt-1">Photos uploaded by talent will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {eventMedia.map((event) => (
                  <Card
                    key={event.gig_id}
                    className="bg-white cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => { setSelectedMedia(event); fetchMediaDetail(event.gig_id); }}
                    data-testid="media-event-card"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-16 h-16 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                          <Camera className="w-6 h-6 text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 truncate">{event.venue_name || event.gig_title}</h4>
                          <p className="text-sm text-slate-500">{event.shift_date ? formatDateMMDDYYYY(event.shift_date) : 'Recent'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="bg-purple-100 text-purple-700">{event.media_count} photos</Badge>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Training Results Tab */}
          <TabsContent value="training">
            {(!Array.isArray(quizResults) || quizResults.length === 0) ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center text-slate-400">
                  <GraduationCap className="w-10 h-10 mx-auto mb-3" />
                  <p className="font-medium">No training results yet</p>
                  <p className="text-sm mt-1">Create quizzes and assign them to your events</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate('/quizzes/manage')}
                    data-testid="manage-quizzes-btn"
                  >
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Manage Quizzes
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {quizResults.map((result, idx) => (
                  <Card key={result.id || idx} className="bg-white" data-testid="quiz-result-card">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900">{result.quiz_title || 'Quiz'}</h4>
                          <p className="text-sm text-slate-500">
                            {result.talent_name || `${result.first_name || ''} ${result.last_name || ''}`}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {result.completed_at ? formatFullDateTime12h(result.completed_at) : 'In progress'}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className={`text-xl font-bold ${
                            (result.score_percent || 0) >= 80 ? 'text-green-600' :
                            (result.score_percent || 0) >= 60 ? 'text-amber-600' : 'text-red-500'
                          }`}>
                            {result.score_percent != null ? `${Math.round(result.score_percent)}%` : '-'}
                          </div>
                          <p className="text-xs text-slate-500">
                            {result.score}/{result.total_points || result.max_score} points
                          </p>
                          <Badge className={`mt-1 ${
                            result.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {result.passed ? 'Passed' : 'Failed'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Media Detail Dialog */}
      <Dialog open={!!selectedMedia} onOpenChange={() => { setSelectedMedia(null); setMediaDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-purple-500" />
              {selectedMedia?.venue_name || 'Event Photos'}
            </DialogTitle>
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
          </DialogHeader>
          {loadingDetail ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
            </div>
          ) : mediaDetail ? (
            <div className="space-y-4">
              {/* Event Info */}
              <div className="flex items-center gap-4 text-sm text-slate-500 pb-3 border-b">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDateMMDDYYYY(mediaDetail.shift_date || mediaDetail.event_date) || 'N/A'}
                </span>
                {mediaDetail.total_media && (
                  <Badge variant="outline">{mediaDetail.total_media} items</Badge>
                )}
              </div>
              {/* Photo Grid */}
              {mediaDetail.media && mediaDetail.media.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {mediaDetail.media.map((item) => (
                    <div key={item.id} className="relative group rounded-lg overflow-hidden">
                      <img
                        src={item.media_url || item.url}
                        alt={item.caption || 'Event photo'}
                        className="w-full h-40 object-cover transition-transform group-hover:scale-105"
                      />
                      {item.caption && (
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 p-2">
                          <p className="text-white text-xs truncate">{item.caption}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Image className="w-8 h-8 mx-auto mb-2" />
                  <p>No photos uploaded for this event</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientReports;
